import React from 'react';
import isEmpty from 'lodash/isEmpty';

import {
  OperationUUID,
  FetchOperationsCacheKeys,
  FetchOperationsShowOperation,
  OperationTypes
} from '../../../operationsTypes';
import { UserID } from '../../../../users/usersTypes';
import { TeamNanoID } from '../../../../teams/teamsTypes';
import { CompanyOperationsCompactTableRowOperation } from './CompanyOperationsCompactTableRow.types';
import { TableViewType } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';
import { UpdateOperationModalButtonAccess } from '../../../components/modalButtons/UpdateOperationModalButton';

import { IndexTableCell } from '../../../../common/components/tables/IndexTableCell';
import { IndexTableCellDropdown } from '../../../../common/components/tables/IndexTableCellDropdown';
import { IndexTableCellDateTimeTooltip } from '../../../../common/components/tables/IndexTableCellDateTimeTooltip';
import { IndexTableCellIcon } from '../../../../common/components/tables/IndexTableCellIcon';
import { IndexTableCellUserAvatar } from '../../../../common/components/tables/IndexTableCellUserAvatar';
import { IndexTableRow } from '../../../../common/components/tables/IndexTableRow';

import { CompanyOperationsCompactTableRowDropdownContent } from './components/CompanyOperationsCompactTableRowDropdownContent';
import { CompanyOperationsCompactTableRowFileAttachments } from './components/CompanyOperationsCompactTableRowFileAttachments';

import { ProjectTaskLink } from '../../../../common/helpers/ProjectTaskLink';

import { Translate } from '../../../../../helpers/Translate';

import { IndexTableCellTeamAvatar } from '../../../../common/components/tables/IndexTableCellTeamAvatar';
import { IndexTableEmptyCell } from '../../../../common/components/tables/IndexTableEmptyCell';

import {
  operationTypeIcon,
  operationStatuses
} from '../../../operationsConstants';
import { ProjectPath } from '../../../../projects/ProjectPath';

import { words } from '../../../../../locales/keys';

interface CompanyOperationsCompactTableRowProps {
  operation: CompanyOperationsCompactTableRowOperation;
  clientsIds?: UserID[];
  teamNanoId?: TeamNanoID;
  cacheKeys?: FetchOperationsCacheKeys;
  withUpdateButton?: boolean;
  updateButtonAccess: UpdateOperationModalButtonAccess;
  selectedOperationUuid?: OperationUUID;
  onOperationShow?: FetchOperationsShowOperation;
  onOperationShowMouseOver?: FetchOperationsShowOperation;
}

function CompanyOperationsCompactTableRow({
  operation,
  clientsIds,
  teamNanoId,
  cacheKeys,
  withUpdateButton,
  updateButtonAccess,
  selectedOperationUuid,
  onOperationShow,
  onOperationShowMouseOver
}: CompanyOperationsCompactTableRowProps) {
  const withDropdown = withUpdateButton;

  return (
    <IndexTableRow
      scope="operation"
      viewType={TableViewType.SLIM}
      onRowShow={onOperationShow}
      onRowShowMouseOver={onOperationShowMouseOver}
      itemUuid={operation?.uuid}
      selected={operation.uuid === selectedOperationUuid}
    >
      {operation.team ? (
        <IndexTableCellTeamAvatar
          team={operation.team}
          avatarType="xs"
          tdClassName="px-3 py-0.5 text-xs"
        />
      ) : (
        <IndexTableCellUserAvatar
          user={operation.client}
          avatarType="xs"
          tdClassName="px-3 py-0.5 text-xs"
        />
      )}
      <IndexTableCellIcon
        icon={operationTypeIcon[operation.operationType]}
        className="h-5 w-5"
        tdClassName="py-0.5 text-xs px-2"
      />
      <IndexTableCell
        tdClassName="py-0.5 px-2 text-xs"
        divClassName="flex-1 flex-col relative h-full min-h-5 w-full"
      >
        <div className="flex gap-x-2 items-center text-gray-500">
          <p className="relative max-w-fit text-xs font-semibold text-gray-900 dark:text-gray-100 line-clamp-1 whitespace-nowrap overflow-ellipsis line">
            {operation.subject}
          </p>
          <p className="w-0 grow max-w-max truncate text-xs text-gray-500 dark:text-gray-400">
            {operation.body}
          </p>
          {operation.project || operation.task ? (
            <span className="text-xs space-x-1 flex w-0 min-w-40 grow max-w-max">
              <span>
                <Translate id={words.in} />
              </span>
              <ProjectTaskLink
                project={operation.project}
                task={operation.task}
                projectClassName="hover:underline text-blue-600 dark:text-blue-300 font-semibold truncate"
                className="hover:underline font-semibold truncate"
                separatorClassName="px-1 text-gray-400"
                getProjectPath={ProjectPath.messages}
              />
            </span>
          ) : null}
        </div>

        {!isEmpty(operation.fileAttachments) && (
          <CompanyOperationsCompactTableRowFileAttachments
            operation={operation}
          />
        )}
      </IndexTableCell>

      <IndexTableCell
        tdClassName="py-1 px-2 text-xs"
        divClassName="text-gray-900 dark:text-gray-200 whitespace-nowrap font-normal"
      >
        <Translate id={operationStatuses[operation.state]} />
      </IndexTableCell>
      <IndexTableCellUserAvatar
        user={operation.user}
        avatarType="xs"
        tdClassName="px-3 py-0.5 text-xs"
      />
      <IndexTableCellDateTimeTooltip
        date={operation.createdAt}
        tdClassName="relative whitespace-nowrap py-1 px-2 text-xs"
        divClassName="flex items-center gap-x-1"
      />
      <IndexTableCellDateTimeTooltip
        date={operation.completeDatetime}
        tdClassName="relative whitespace-nowrap py-1 px-2 text-xs"
        divClassName="flex items-center gap-x-1"
      />
      <IndexTableCellDateTimeTooltip
        date={operation.date}
        tdClassName="relative whitespace-nowrap py-1 px-2 text-xs"
        divClassName="flex items-center gap-x-1"
      />
      {withDropdown &&
      operation.operationType !== OperationTypes.CLIENT_CUSTOM_STATUS ? (
        <IndexTableCellDropdown
          icon={IconsEnum.DOTS_HORIZONTAL_OUTLINE}
          tabIndex={0}
          viewType={TableViewType.SLIM}
        >
          <CompanyOperationsCompactTableRowDropdownContent
            operation={operation}
            cacheKeys={cacheKeys}
            clientsIds={clientsIds}
            teamNanoId={teamNanoId}
            withUpdateButton={withUpdateButton}
            updateButtonAccess={updateButtonAccess}
          />
        </IndexTableCellDropdown>
      ) : null}

      {withDropdown &&
      operation.operationType === OperationTypes.CLIENT_CUSTOM_STATUS ? (
        <IndexTableEmptyCell />
      ) : null}
    </IndexTableRow>
  );
}

export default CompanyOperationsCompactTableRow;
